/**
 * How many people a campaign reaches, and how many will actually receive it.
 *
 * This is the number slice 2 settled and slice 5 has to keep: **the reach is the
 * channel reach, never the segment size.** A segment of 1,278 unique recipients
 * reaches 526 by email, because the other 752 have a phone number and no email
 * address, and Wend never falls back to the other channel to make a number look
 * bigger. Putting 1,278 next to a Schedule button would let someone schedule
 * believing they are reaching 1,278 people.
 *
 * The segment's own totals are still on the panel, deliberately — underneath, in
 * small type, labelled as context. Hiding them would leave a person unable to
 * reconcile "1,287 contacts" on the segment screen with "526" here, which is the
 * other way to lose their trust in the number.
 *
 * **The second figure is the one that stops a person concluding the product is
 * broken.** `reachable` is a ceiling, not a promise: the send guard cuts it
 * further, and in a guarded environment it usually cuts it to almost nothing. So
 * the deliverable count is shown beside the reach, at the same size, **whenever
 * the guard is actually applying one** — never behind a click, and never left
 * for someone to discover after the send.
 *
 * That qualifier is slice 25's and it replaces "wherever the API has computed
 * one", which was the bug. `deliverable` is computed whether or not the guard is
 * switched on, and with it off it comes back equal to `reachable` — so on the
 * deployment that actually sends, this panel drew the same number twice at
 * 2.1rem with an arrow between them, and attributed the second one to a
 * mechanism that was doing nothing. `sendGuardStatusSchema` had already said
 * `enforced` must be read first; this file was the consumer that did not. See
 * `CampaignReachPanel`, where the three states are spelled out.
 *
 * **Slice 66 drew it as a figure panel**, the shared `MetricPanel`: sans tabular
 * digits rather than a 2.1rem mono numeral (mono's dotted zero reads as an 8),
 * and a meter under the reach. The meter is reach ÷ the segment's people —
 * magnitude only, never a status colour — and it is the one place on the panel
 * that *shows* the gap the sentence below states: `reachable` and
 * `segmentUniqueRecipients` are both counts of distinct people-or-addresses, so
 * the ratio is meaningful, which `segmentContacts` (rows) would not be. The gap
 * sentence itself became a compact `Callout`: people this channel cannot touch
 * is the one fact on the panel a sender must not skim past, and a muted
 * paragraph was exactly what got skimmed. The segment's two totals stay as
 * labelled context in a section of the panel, small and never the headline.
 *
 * Every figure comes from the API on the request that drew this panel:
 * `reach` is `campaignReach()`'s, `sendGuard.deliverable` is the guard's own
 * count of how many of these addresses it will actually let through.
 */
import type { CampaignReach, SendGuardStatus } from '@wend/shared';
import { Callout, MetricPanel, MetricSection, cn } from '@wend/ui';

import { CHANNEL_LABEL, describeReach } from '../lib/campaign-display';
import { formatCount } from '../lib/format';


/* -------------------------------------------------------------------------- */
/* The panel                                                                   */
/* -------------------------------------------------------------------------- */

export interface CampaignReachPanelProps {
  reach: CampaignReach;
  /**
   * The guard's verdict on this campaign, when the API has computed one.
   *
   * **`enforced` decides what is drawn, never `deliverable` alone.** With the
   * guard enforced the deliverable count is the second figure and it is the one
   * the eye lands on, because it is the only one of the two that is a promise.
   * With it lifted — which is how the deployment that actually sends is
   * configured — `deliverable` comes back equal to `reachable`, and a second
   * figure there would be the same number twice crediting a mechanism that is
   * doing nothing. That state gets one figure and a chip instead.
   *
   * This paragraph used to say "when the API has computed one, the deliverable
   * count is shown as the second figure", full stop. That sentence *was* the
   * defect, and it is restated here rather than quietly replaced because a
   * maintainer who reads a prop contract and not a file header is exactly who
   * would otherwise delete the `enforced` check to make the code match its
   * documentation. See `CampaignReachPanel` for the three states.
   */
  sendGuard?: SendGuardStatus | null;
  className?: string;
}

export function CampaignReachPanel({ reach, sendGuard, className }: CampaignReachPanelProps) {
  const channel = CHANNEL_LABEL[reach.channel];
  const deliverable = sendGuard?.deliverable ?? null;

  /*
   * `enforced` IS READ FIRST, WHICH THE CONTRACT ASKS FOR AND THIS FILE DID NOT.
   *
   * `sendGuardStatusSchema` says it in the field's own comment — *"Whether
   * SEND_ALLOWLIST is being applied at all. **Read this first.** False means
   * every reachable address is deliverable and the allow-list is inert"* — and
   * its header adds that a sentence about an allow-list drawn without checking
   * `enforced` is describing a list nobody is consulting. This panel read
   * `deliverable` alone.
   *
   * What that looked like on the deployment that sends, where the guard is
   * lifted on purpose: `deliverable` comes back equal to `reachable`, so
   * **Reachable by email 1,184** and **Will receive it 1,184** rendered side by
   * side at 2.1rem with an arrow between them, under a caption about a guard
   * that was doing nothing. Two hero figures, one number, and a promise
   * attributed to a mechanism that was switched off. `wend-designer` found it.
   *
   * Now the second figure is drawn only while the guard is *enforced* — where it
   * is a real second number, even when it happens to equal the first, because
   * then the equality is the news. With the guard off, one figure and a chip:
   * the guard was consulted, it let everybody through, and the panel says so in
   * two words rather than by repeating a number.
   */
  const enforced = sendGuard?.enforced ?? false;
  const guardFiltering = deliverable !== null && enforced;
  const guardInert = deliverable !== null && !enforced;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <MetricPanel
        className="max-w-xl"
        kicker={`Reachable by ${channel}`}
        figure={{ kind: 'measured', value: reach.reachable }}
        figureSize="lg"
        sub={`People this campaign can reach by ${channel}`}
        // The violet panel marks the number to leave with; while the guard is
        // filtering it is a ceiling rather than a promise, so it steps down to
        // the plain skin — the same rule `emphasis` carried before slice 66.
        variant={guardFiltering ? 'plain' : 'brand'}
        {...(reach.segmentUniqueRecipients > 0
          ? { meter: { value: reach.reachable / reach.segmentUniqueRecipients } }
          : {})}
      >
        {/* Context, never the headline: two counts of the segment, kept small and
            explicitly labelled so nobody reads either as a send size. */}
        <MetricSection kicker="The segment, for context">
          <p className="text-[0.8rem] text-muted tabular-nums">
            {formatCount(reach.segmentContacts)} contacts · {formatCount(reach.segmentUniqueRecipients)}{' '}
            people
          </p>
        </MetricSection>

        {/*
          "WILL RECEIVE IT" AND "ALL DELIVERABLE" WENT — SLICE 28.

          The first was a second 2.1rem hero figure, at the same visual rank as
          the real audience number, whose value is decided by an environment
          variable the sender cannot change and CLAUDE.md forbids anyone from
          changing. The second was a chip confirming that a mechanism the sender
          has never heard of did nothing — pure noise on the only deployment
          senders use.

          The guard is a property of the DEPLOYMENT, not of this campaign, so it
          is said once where deployments are described: `SendGuardNotice`, now
          rendered at the top of the page rather than per figure. The skip
          breakdown in Progress still carries `not_on_send_allowlist` with its own
          sentence and its own link to the people it held back, so no count is
          lost.
        */}
      </MetricPanel>

      {/*
        `describeReach` keeps the branch that reports a GAP — people in the
        segment with no address on this channel — and loses the branch that said
        *"Every one of the 1,118 people in this segment has an email address"*,
        which restates the figure directly above it as a sentence and adds
        nothing. The owner quoted that one. Since slice 66 the gap is a compact
        `info` callout rather than a muted paragraph: it is never dropped, and
        it is not a warning either — nothing is wrong, a channel simply cannot
        reach somebody with no address on it.
      */}
      {reach.reachable < reach.segmentContacts && (
        <Callout tone="info" density="compact" className="max-w-xl">
          {describeReach(reach)}
        </Callout>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Compact, for a table row                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The reach in one cell.
 *
 * The channel is named on the line rather than assumed, because the same column
 * will carry a WhatsApp campaign the day slice 7 lands, and "752" with no
 * channel beside it is the exact ambiguity this whole shape exists to close.
 */
export function CampaignReachCell({ reach }: { reach: CampaignReach }) {
  return (
    <div>
      <p className="text-[0.88rem] font-medium tabular-nums text-text">
        {formatCount(reach.reachable)}{' '}
        <span className="text-muted">by {CHANNEL_LABEL[reach.channel]}</span>
      </p>
      {/* Sans tabular, not the mono caps it was: a count in mono reads its
          dotted zero as an 8 (the Blueprint's R6; slice 66 review). */}
      <p className="text-[0.76rem] tabular-nums text-muted">
        of {formatCount(reach.segmentUniqueRecipients)} in segment
      </p>
    </div>
  );
}
